// src/api/axiosInstance.js

import axios from "axios";
import { toast } from "react-toastify";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// 🔹 DEV-ONLY: Real-time API Documentation & Logging
const apiDoc = [];
let swaggerMap = {}; // Will hold mapping from backend

// Fetch Swagger mapping once at startup (dev only)
async function loadSwaggerMapping() {
  // if (process.env.NODE_ENV !== "development") return;

  try {
    const res = await axios.get(`${process.env.REACT_APP_API_GATEAWAY}/swagger-mapping`);
    swaggerMap = res.data;
    console.log("✅ Swagger mapping loaded", swaggerMap);
  } catch (err) {
    console.warn("⚠️ Failed to load Swagger mapping:", err);
  }
}
loadSwaggerMapping();

// Helper to normalize dynamic paths
function normalizePath(url) {
  try {
    const baseUrl = process.env.REACT_APP_API_GATEAWAY;
    let pathOnly = url.startsWith("http") ? url.replace(baseUrl, "") : url;

    // Remove query string
    pathOnly = pathOnly.split("?")[0];

    const segments = pathOnly.split("/").map((seg) => {
      if (!seg) return null;

      // 1️⃣ Numeric IDs
      if (/^\d+$/.test(seg)) return "{param}";

      // 2️⃣ Prefixed IDs  
      if (/^(USER|COMPANY|MEMBER|SUBSCRIBER|APP|CUSTOMER_GROUP|REPORT_GROUP|NOTIFICATION_GROUP|GROUP|REGION|ROUTE|ASSET|RECIPIENT)-[A-Za-z0-9-]+$/.test(seg))
        return "{param}";

      // 3️⃣ cmip* IDs
      if (/^cmip[a-z0-9]+$/i.test(seg)) return "{param}";

      // 4️⃣ UUIDs
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg))
        return "{param}";
      // 5️⃣ Mixed numeric-uppercase IDs (e.g. 01505001SKYE4CA)
      if (/^\d+[A-Z0-9]+$/.test(seg)) return "{param}";

      // Otherwise, leave segment intact (resource names)
      return seg;
    }).filter(Boolean);

    return "/" + segments.join("/"); // always start with /
  } catch (err) {
    console.warn("Failed to normalize path:", url, err);
    return url;
  }
}

// Logger function
function logApi(config, response = null, error = null) {
  const normalizedUrl = normalizePath(config.url);
  const key = `${config.method?.toUpperCase()} ${normalizedUrl}`;

  const swaggerEntry = swaggerMap[key] || null;
  const errorStatus = error?.response?.status || null;

  const logEntry = {
    method: config.method?.toUpperCase(),
    url: config.url,
    normalizedUrl,
    page: window.location.pathname,
    timestamp: new Date().toISOString(),
    requestData: config.data || null,
    responseData: response?.data || null,
    errorStatus,
    errorMessage: error?.response?.data?.error || error?.message || null,
    swaggerUrl: swaggerEntry?.swaggerUrl,
    swaggerSummary: swaggerEntry?.summary,
    swaggerDescription: swaggerEntry?.description,
  };

  apiDoc.push(logEntry);

  // 🎯 Decide icon + style
  let icon = "🟢";
  let groupStyle = "color: #4caf50; font-weight: 600";

  if (error) {
    icon = "🔴";
    groupStyle = "color: #f44336; font-weight: 700";
  } else if (errorStatus >= 400) {
    icon = "🟠";
    groupStyle = "color: #ff9800; font-weight: 700";
  }

  console.groupCollapsed(
    `%c${icon} API ${logEntry.method} ${logEntry.url} (${logEntry.swaggerSummary || "No summary"})`,
    groupStyle
  );

  console.log("📍 Page:", logEntry.page);

  if (logEntry.requestData)
    console.log("➡️ Request Data:", logEntry.requestData);

  if (response)
    console.log("✅ Response Data:", logEntry.responseData);

  if (error)
    console.log(
      "%c❌ Error:",
      "color: #f44336; font-weight: bold",
      logEntry.errorMessage,
      "Status:",
      logEntry.errorStatus
    );

  if (logEntry.swaggerUrl)
    console.log("🔗 Swagger URL:", logEntry.swaggerUrl);

  // if (logEntry.swaggerDescription)
  //   console.log("📘 Swagger Description:", logEntry.swaggerDescription);
  console.groupEnd();
}



// REQUEST INTERCEPTOR
axiosInstance.interceptors.request.use(
  (config) => {

    const token = localStorage.getItem("token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// RESPONSE INTERCEPTOR
axiosInstance.interceptors.response.use(
  (response) => {
    logApi(response.config, response);
    return response;
  },
  (error) => {
    logApi(error.config, null, error);
    console.log("❌ Response error:", error.response?.status);
    console.log("Full error:", error);
    if (error.response && error.response.status === 401) {
      console.log("🚨 401 detected - logging out!");
      localStorage.clear();
      toast.error("Session expired. Please login again.");
      window.location.href = "/signin";
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
